import React from 'react';
import { Button } from '@/components/ui/button';
import { Download, X, Share } from 'lucide-react';
import { usePwaInstallPrompt, isIosSafari } from '@/hooks/usePwaInstallPrompt';

function IosHelpBody() {
  return (
    <div className="mt-3 p-3 rounded-lg bg-muted/50 text-xs text-foreground space-y-1.5">
      <div className="flex items-start gap-2">
        <span className="font-semibold">1.</span>
        <span>Tap the <Share className="inline w-3.5 h-3.5 mx-1 -mt-0.5" /> Share button below.</span>
      </div>
      <div className="flex items-start gap-2">
        <span className="font-semibold">2.</span>
        <span>Choose <b>Add to Home Screen</b>.</span>
      </div>
      <div className="flex items-start gap-2">
        <span className="font-semibold">3.</span>
        <span>Tap <b>Add</b> in the top-right.</span>
      </div>
    </div>
  );
}

function InstallActions({ onInstall, onDismiss }) {
  return (
    <div className="mt-3 flex gap-2">
      <Button data-testid="pwa-install-btn" onClick={onInstall} size="sm" className="flex-1 rounded-full">
        <Download className="w-4 h-4 mr-1" /> Install
      </Button>
      <Button data-testid="pwa-install-later" onClick={onDismiss} variant="outline" size="sm" className="rounded-full">
        Not now
      </Button>
    </div>
  );
}

export function PwaInstallPrompt() {
  const { visible, showIosHelp, install, dismiss, deferredEvent } = usePwaInstallPrompt();
  if (!visible) return null;

  const iosMode = showIosHelp || (isIosSafari() && !deferredEvent);
  const descText = iosMode
    ? 'Add to your home screen for one-tap access and full-screen browsing.'
    : 'Add to your device home screen for one-tap access.';

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed left-3 right-3 md:left-auto md:right-6 md:w-96 z-50 rounded-2xl shadow-xl border border-border bg-white overflow-hidden"
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 88px)' }}
      role="dialog"
      aria-label="Install PrimePocket"
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <Download className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm">Install PrimePocket</div>
            <p className="text-xs text-muted-foreground mt-0.5">{descText}</p>
          </div>
          <button
            data-testid="pwa-install-dismiss"
            onClick={dismiss}
            className="p-1 -mt-1 -mr-1 rounded-md hover:bg-muted text-muted-foreground"
            aria-label="Dismiss install prompt"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {iosMode ? <IosHelpBody /> : <InstallActions onInstall={install} onDismiss={dismiss} />}
      </div>
    </div>
  );
}
